// src/components/page-content-v4/modules/PageSettingsPanel.tsx
import React from 'react';
import { Clock, User, Save, Trash2, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { PageData } from '../types';

interface PageSettingsPanelProps {
  page: PageData;
  isDraftEditing: boolean;
  onSaveDraft: () => void;
  onPublish: () => void;
  onDiscardDraft: () => void;
  onDelete?: () => void;
}

const PageSettingsPanel: React.FC<PageSettingsPanelProps> = ({
  page,
  isDraftEditing,
  onSaveDraft,
  onPublish,
  onDiscardDraft,
  onDelete,
}) => {
  const BUTTON_STYLES = {
    primary: `w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg 
      text-white bg-primary-500 hover:bg-primary-600 transition-colors`,
    secondary: `w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg 
      text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors`,
    danger: `w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg 
      text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors`,
  };

  return (
    <div className="w-full lg:w-80 space-y-4 sticky top-20">
      {/* Page Status Card */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">Page Status</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Status</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              isDraftEditing 
                ? 'bg-yellow-100 text-yellow-700' 
                : page.isDraft 
                  ? 'bg-gray-100 text-gray-700' 
                  : 'bg-green-100 text-green-700'
            }`}>
              {isDraftEditing ? 'Draft' : page.isDraft ? 'Unpublished' : 'Published'}
            </span>
          </div>
          
          {page.lastUpdatedBy && (
            <div className="flex items-start justify-between gap-2">
              <span className="text-gray-600 flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                Updated by
              </span>
              <span className="text-gray-900 text-right">{page.lastUpdatedBy}</span>
            </div>
          )}
          
          {page.updatedAt && (
            <div className="flex items-start justify-between gap-2">
              <span className="text-gray-600 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                Last updated
              </span>
              <span className="text-gray-900 text-right">
                {formatDistanceToNow(new Date(page.updatedAt), { addSuffix: true })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions Card */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">Actions</h3>
        <div className="space-y-2">
          <button
            onClick={onSaveDraft}
            className={BUTTON_STYLES.secondary}
          >
            <Save className="mr-2 h-4 w-4" />
            Save as Draft
          </button>
          
          {isDraftEditing ? (
            <>
              <button
                onClick={onPublish}
                className={BUTTON_STYLES.primary}
              >
                <Save className="mr-2 h-4 w-4" />
                Publish
              </button>
              <button
                onClick={onDiscardDraft}
                className={BUTTON_STYLES.danger}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Discard Draft
              </button>
            </>
          ) : (
            <button
              onClick={onPublish}
              className={BUTTON_STYLES.primary}
            >
              <Save className="mr-2 h-4 w-4" />
              Save & Publish
            </button>
          )}
          
          {onDelete && (
            <button
              onClick={onDelete}
              className={BUTTON_STYLES.danger}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Page
            </button>
          )}
        </div>
      </div>

      {/* Language & Locale Info */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">Locale</h3>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Language</span>
          <span className="text-gray-900 font-medium uppercase">{page.language || 'en'}</span>
        </div>
        {page.isPrimary !== undefined && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Primary</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              page.isPrimary ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {page.isPrimary ? 'Yes' : 'No'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PageSettingsPanel;
